import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useRef } from 'react';
import { useBrand } from './brand-context';
import { isSlateInventoryType } from './is-slate-inventory-type';

/**
 * Domain-locking is what makes synchronous brand resolution safe: for real customer
 * traffic the vehicle's inventory type always agrees with the domain. Nothing in the
 * codebase enforces that, so report disagreement instead of assuming it.
 *
 * Impersonation is exempt — advocates reach a Slate vehicle from the Carvana domain by
 * design, so disagreement there is expected rather than a defect.
 */
const useBrandConsistencyCheck = (
  inventoryType: string | null | undefined,
  isImpersonating: boolean
): void => {
  const { brand } = useBrand();
  const reported = useRef<string | null>(null);

  useEffect(() => {
    if (inventoryType === undefined || inventoryType === null) return;
    if (isImpersonating) return;

    const vehicleBrand = isSlateInventoryType(inventoryType) ? 'slate' : 'carvana';
    if (vehicleBrand === brand) return;

    const signature = `${brand}:${vehicleBrand}`;
    if (reported.current === signature) return;
    reported.current = signature;

    datadogRum.addError(new Error('brand and inventory type disagree'), {
      customType: 'brandConsistency',
      resolvedBrand: brand,
      inventoryType
    });
  }, [brand, inventoryType, isImpersonating]);
};

export { useBrandConsistencyCheck };
